import type { TFunction } from 'i18next';
import { observer } from 'mobx-react-lite';
import numeral from 'numeral';
import type { JSX } from 'react';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  AggregatedStat,
  Campaign,
  IPersonsListParams,
  IPredicate,
  TChannelKey,
} from '@feathr/blackbox';
import { EReportFlavorsRaw, reportModuleLabels } from '@feathr/blackbox';
import type { ISelectOption, TCollectionTableSort } from '@feathr/components';
import {
  EmptyState,
  TableStatsCard,
  TanStackTable,
  Value,
  VerifiedActivityToggle,
} from '@feathr/components';
import { EEmailStats, getEmailStats } from '@feathr/report_components/components/EmailStats';
import { useStore } from '@feathr/report_components/state';

import { ReportContext } from '../Report.context';
import campaignEngagementColumns from './campaignEngagementColumns';
import CampaignInteractionSelect from './CampaignInteractionSelect';
import CreateGroupButton from './CreateGroupButton';

import * as styles from './CampaignEngagementCard.css';

interface IProps {
  activeChannel?: TChannelKey;
  aggregatedStats: AggregatedStat;
  campaign: Campaign;
  localUrl: ((url: string) => string) | undefined;
  dateRange: {
    start: string;
    end: string;
  };
}

interface IEngagementPredicate {
  docvalue_fields: string[];
  name: string;
  size: number;
  sort: {
    'breadcrumbs.d_c': string;
  };
}

export interface IEngagementPredicateGroup {
  group: IPredicate[];
  inner_hits: IEngagementPredicate;
}

// Override predicates field typing on IPersonsListParams to allow for custom query.
export interface IEngagementParams extends Omit<IPersonsListParams, 'predicates'> {
  /*
   * We can sort on specific fields on people, in this case we are sorting on
   * person.inner_hits to get the person's first crumb for a given flavor and campaign.
   */
  predicates: IPredicate[] | IEngagementPredicateGroup[];
}

export const flavorToInteractionMap = (t: TFunction): Record<string, string> => ({
  // Ad campaigns
  [EReportFlavorsRaw.ad_view]: t('Viewed ad'),
  [EReportFlavorsRaw.ad_click]: t('Clicked ad'),
  // Bots
  [EReportFlavorsRaw.bot_pinpoint_tracked_email_click]: t('Bot clicked email'),
  [EReportFlavorsRaw.bot_pinpoint_tracked_email_open]: t('Bot opened email'),
  // Email campaigns
  [EReportFlavorsRaw.pinpoint_tracked_email_send]: t('Sent'),
  [EReportFlavorsRaw.pinpoint_tracked_email_hardbounce]: t('Hard bounce'),
  [EReportFlavorsRaw.pinpoint_tracked_email_softbounce]: t('Soft bounce'),
  [EReportFlavorsRaw.pinpoint_tracked_email_delivered]: t('Received'),
  [EReportFlavorsRaw.pinpoint_tracked_email_open]: t('Opened'),
  [EReportFlavorsRaw.pinpoint_tracked_email_click]: t('Clicked'),
  [EReportFlavorsRaw.pinpoint_tracked_email_complaint]: t('Reported as spam'),
  [EReportFlavorsRaw.email_not_sent_via_opt_out]: t('Not sent via opt out'),
  [EReportFlavorsRaw.email_preferences_update]: t('Unsubscribed from all'),
});

const {
  Complaints,
  Deliveries,
  HardBounces,
  NotSentViaOptOut,
  Sends,
  SoftBounces,
  UniqueClicks,
  UniqueOpens,
} = EEmailStats;

const emailFlavorToStatMap: Record<string, string> = {
  [EReportFlavorsRaw.pinpoint_tracked_email_hardbounce]: HardBounces,
  [EReportFlavorsRaw.pinpoint_tracked_email_send]: Sends,
  [EReportFlavorsRaw.pinpoint_tracked_email_softbounce]: SoftBounces,
  [EReportFlavorsRaw.pinpoint_tracked_email_delivered]: Deliveries,
  [EReportFlavorsRaw.pinpoint_tracked_email_open]: UniqueOpens,
  [EReportFlavorsRaw.pinpoint_tracked_email_click]: UniqueClicks,
  [EReportFlavorsRaw.pinpoint_tracked_email_complaint]: Complaints,
  [EReportFlavorsRaw.email_not_sent_via_opt_out]: NotSentViaOptOut,
};

const adFlavorToStatMap: Record<string, string> = {
  [EReportFlavorsRaw.ad_view]: 'views',
  [EReportFlavorsRaw.ad_click]: 'clicks',
};

function getAdStats(stats: AggregatedStat): Record<string, number> {
  return {
    // We want unique clicks, not total clicks
    clicks: stats.get('num_clicks_active', 0),
    views: stats.get('num_users_active', 0),
  };
}

export function getPredicates(
  campaignId: string,
  flavor: EReportFlavorsRaw,
  dateStart?: string,
  dateEnd?: string,
  includeBots = false,
  adGroup?: string,
): IPredicate[] | IEngagementPredicateGroup[] {
  const flavorIsUnsubscribe = flavor === EReportFlavorsRaw.email_preferences_update;

  // Create array to hold predicate groups
  const predicateGroups: IEngagementPredicateGroup[] = [];

  // Helper function to create a predicate group for a specific flavor
  const createGroup = (flavorValue: EReportFlavorsRaw): IEngagementPredicateGroup => {
    const group: IPredicate[] = [
      {
        kind: 'activity',
        attr_against: 'cpn_id',
        attr_type: 'string',
        comparison: 'eq',
        value: campaignId,
      },
      {
        kind: 'activity',
        attr_against: 'flvr',
        attr_type: 'string',
        comparison: 'eq',
        value: flavorValue,
      },
    ];

    if (adGroup) {
      group.push({
        kind: 'activity',
        attr_against: 'ad_group',
        attr_type: 'string',
        comparison: 'eq',
        value: adGroup,
      });
    }

    if (dateStart) {
      group.push({
        kind: 'activity',
        attr_against: 'd_c',
        attr_type: 'date',
        comparison: 'gte_date',
        value: dateStart,
      });
    }
    if (dateEnd) {
      group.push({
        kind: 'activity',
        attr_against: 'd_c',
        attr_type: 'date',
        comparison: 'lte_date',
        value: dateEnd,
      });
    }
    if (flavorIsUnsubscribe) {
      group.push({
        kind: 'activity',
        attr_against: 'msg',
        attr_type: 'string',
        comparison: 'contains_substring',
        value: '"global": true',
      });
    }

    return {
      group,
      inner_hits: {
        name: 'first_flavor',
        sort: {
          'breadcrumbs.d_c': 'asc',
        },
        size: 1,
        docvalue_fields: ['breadcrumbs.d_c'],
      },
    };
  };

  predicateGroups.push(createGroup(flavor));

  // Add bot flavor predicate group if needed
  if (includeBots) {
    if (flavor === EReportFlavorsRaw.pinpoint_tracked_email_open) {
      predicateGroups.push(createGroup(EReportFlavorsRaw.bot_pinpoint_tracked_email_open));
    } else if (flavor === EReportFlavorsRaw.pinpoint_tracked_email_click) {
      predicateGroups.push(createGroup(EReportFlavorsRaw.bot_pinpoint_tracked_email_click));
    }
  }

  return predicateGroups;
}

function CampaignEngagementCard({
  activeChannel,
  aggregatedStats,
  campaign,
  dateRange,
  localUrl,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { Persons } = useStore();

  const { prioritizeVerifiedActivity, setPrioritizeVerifiedActivity } =
    useContext(ReportContext) ?? {};

  const {
    ad_view: adView,
    email_preferences_update: emailPreferencesUpdate,
    pinpoint_tracked_email_send: emailSend,
  } = EReportFlavorsRaw;
  const [flavor, setFlavor] = useState(campaign.isAdCampaign ? adView : emailSend);
  const flavorIsUnsubscribe = flavor === emailPreferencesUpdate;
  const adGroup = campaign.isMailrailEmail && activeChannel ? activeChannel : undefined;

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState<TCollectionTableSort>([]);

  const isMetaTab = activeChannel === 'meta';

  if (isMetaTab) {
    return (
      <TableStatsCard title={reportModuleLabels.includeCampaignEngagement}>
        <EmptyState
          description={t("Meta doesn't share per-person click identity.")}
          label={t('No data available')}
          theme={'white'}
          width={'full'}
        />
      </TableStatsCard>
    );
  }

  const ordering = sort.length
    ? sort.map(({ id, desc }) => `${desc ? '-' : ''}${id}`)
    : ['-date_last_seen'];

  const params = (function (): IEngagementParams {
    const { start: dateStart, end: dateEnd } = dateRange;

    const predicates = getPredicates(
      campaign.id,
      flavor,
      dateStart,
      dateEnd,
      !prioritizeVerifiedActivity,
      adGroup,
    );

    return {
      mode: 'match_any',
      lookback_mode: 'unbounded',
      exclude: ['breadcrumbs'],
      ordering,
      pagination: { page, page_size: pageSize },
      predicates,
    };
  })();

  function handleSelectFlavor({ id }: ISelectOption): void {
    setFlavor(id as EReportFlavorsRaw);
    setPage(0);
  }

  function handlePageChange(newPage: number): void {
    setPage(newPage);
  }

  function handlePageSizeChange(newPageSize: number, newPage: number): void {
    setPageSize(newPageSize);
    setPage(newPage);
  }

  function handleSortChange(newSorted: TCollectionTableSort): void {
    setSort(newSorted);
    // A new ordering invalidates the previous page's row positions.
    setPage(0);
  }

  const [map, statFunc] = campaign.isAdCampaign
    ? [adFlavorToStatMap, getAdStats]
    : [emailFlavorToStatMap, getEmailStats];

  // Do not show the create group button on anonymous reports
  const isAnonymousReport = !localUrl;
  const actions = !isAnonymousReport
    ? [
        <CreateGroupButton
          adGroup={adGroup}
          campaign={campaign}
          dateEnd={dateRange.end}
          dateStart={dateRange.start}
          flavor={flavor}
          key={'create-group-button'}
          localUrl={localUrl}
        />,
      ]
    : undefined;

  // Only show the verified activity toggle for unique opens and clicks
  const selectedRelevantFlavor = map[flavor] === UniqueOpens || map[flavor] === UniqueClicks;
  if (!campaign.isAdCampaign && !isAnonymousReport && selectedRelevantFlavor) {
    actions?.unshift(
      <VerifiedActivityToggle
        key={'verified-activity-toggle'}
        onChange={setPrioritizeVerifiedActivity}
        value={prioritizeVerifiedActivity}
      />,
    );
  }

  const personsList = Persons.list(params, { url: Persons.url('page') });

  // Unique unsubscribes aren't on AggregatedStats, so reuse the table's list call.
  const uniquePersons = (function (): { number: string; count: number } {
    const total = flavorIsUnsubscribe ? personsList : statFunc(aggregatedStats)[map[flavor]];

    return flavorIsUnsubscribe
      ? {
          number: numeral(total.isPending ? '-' : total.pagination.count).format('0,0'),
          count: total.isPending ? 0 : total.pagination.count,
        }
      : {
          number: numeral(total).format('0,0'),
          count: total,
        };
  })();

  return (
    <TableStatsCard actions={actions} title={reportModuleLabels.includeCampaignEngagement}>
      <div className={styles.header}>
        <div className={styles.selectWrapper}>
          <CampaignInteractionSelect
            campaign={campaign}
            flavor={flavor}
            onSelectSingle={handleSelectFlavor}
          />
          <Value
            className={styles.totalInteractions}
            label={
              [
                EReportFlavorsRaw.pinpoint_tracked_email_hardbounce,
                EReportFlavorsRaw.pinpoint_tracked_email_softbounce,
              ].includes(flavor)
                ? t('Unique {{flavor}}d:', { flavor: flavorToInteractionMap(t)[flavor] })
                : t('Unique {{flavor}}:', { flavor: flavorToInteractionMap(t)[flavor] })
              // TODO: This is not good localization
            }
            value={t('{{count}} Person', {
              count: uniquePersons.count,
            })}
          />
        </div>
      </div>
      <TanStackTable
        columns={campaignEngagementColumns(t, localUrl, flavor)}
        idKey={'id'}
        isLoading={personsList.isPending}
        items={personsList.models}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSortChange={handleSortChange}
        page={page}
        pages={personsList.pagination.pages}
        pageSize={pageSize}
        sort={sort}
      />
    </TableStatsCard>
  );
}

export default observer(CampaignEngagementCard);
